import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Sparkles, CalendarPlus, CheckCircle2, FileText, ArrowRight, MapPin, Clock, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CAREGIVER, NAV } from "@/constants/testIds";
import CareOpsPanel from "@/components/CareOpsPanel";
import ReferralBanner from "@/components/ReferralBanner";
import W9Dialog from "@/components/W9Dialog";

export default function CaregiverDashboard() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [shifts, setShifts] = useState([]);
    const [careRequests, setCareRequests] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [parsed, setParsed] = useState(user?.resume_parsed || null);
    const fileRef = useRef(null);
    const [w9Status, setW9Status] = useState(null);
    const [w9Open, setW9Open] = useState(false);

    async function loadW9() {
        try {
            const r = await api.get("/caregiver/w9/status");
            setW9Status(r.data);
        } catch { /* noop */ }
    }
    useEffect(() => { loadW9(); }, []);

    async function load() {
        try {
            const [sh, cr] = await Promise.all([
                api.get("/shifts"),
                api.get("/care/requests"),
            ]);
            setShifts(sh.data);
            setCareRequests(cr.data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, []);
    useEffect(() => { if (user?.resume_parsed) setParsed(user.resume_parsed); }, [user]);

    async function upload(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/resume/parse", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setParsed(data.parsed);
            await refresh();
            toast.success("Resume parsed");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Parse failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    async function connectGoogle() {
        try {
            const { data } = await api.get("/calendar/connect");
            if (data.url) window.location.href = data.url;
            else { toast.success("Google connect simulated (dev mode)"); await refresh(); }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    }

    async function claim(id) {
        try {
            await api.post(`/shifts/${id}/claim`);
            toast.success("Shift claimed");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    }

    async function push(id) {
        try {
            const { data } = await api.post(`/calendar/push-shift/${id}`);
            toast.success(data.mocked ? "Calendar push simulated" : "Pushed to Google Calendar");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    }

    const openShifts = shifts.filter((s) => s.status === "open");
    const mine = shifts.filter((s) => s.caregiver_id === user?.id);

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid={CAREGIVER.dashboard} className="max-w-7xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <ReferralBanner />

                {/* W-9 gate banner — hard-blocks shift acceptance until submitted */}
                {w9Status && !w9Status.uploaded && (
                    <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 flex items-start gap-3" data-testid="w9-banner">
                        <FileText className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="font-outfit font-semibold text-stone-900">Complete your W-9 to start earning</div>
                            <p className="text-sm font-dmsans text-stone-700 mt-1">
                                Infini HealthCare caregivers are independent 1099 contractors. IRS requires a completed W-9 on file before we can pay you. Takes ~2 minutes.
                            </p>
                        </div>
                        <Button
                            onClick={() => setW9Open(true)}
                            data-testid="w9-banner-open-btn"
                            className="rounded-full h-10 px-5 bg-amber-700 hover:bg-amber-800 text-white font-outfit shrink-0"
                        >Complete W-9</Button>
                    </div>
                )}
                {w9Status?.uploaded && (
                    <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-xs font-dmsans text-emerald-900" data-testid="w9-on-file">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>W-9 on file: {w9Status.legal_name} · {w9Status.tin_type} ••••{w9Status.tin_last4}</span>
                        <button onClick={() => setW9Open(true)} className="ml-auto underline text-emerald-800 hover:text-emerald-900" data-testid="w9-edit-btn">Update</button>
                    </div>
                )}
                <W9Dialog open={w9Open} onOpenChange={setW9Open} onSaved={loadW9} />

                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Caregiver workspace</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                        Hi, {user?.full_name}
                    </h1>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Badge
                            data-testid={CAREGIVER.googleStatus}
                            className={user?.google_connected
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                : "bg-stone-100 text-stone-700 border-stone-200 hover:bg-stone-100"}
                        >
                            <CalendarPlus className="w-3.5 h-3.5 mr-1" />
                            Google: {user?.google_connected ? "Connected" : "Not connected"}
                        </Badge>
                        <Badge className={parsed
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                            : "bg-stone-100 text-stone-700 border-stone-200 hover:bg-stone-100"}>
                            <FileText className="w-3.5 h-3.5 mr-1" />
                            Resume: {parsed ? "Parsed" : "Not uploaded"}
                        </Badge>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-6 mb-8">
                    <Card className="rounded-2xl border-stone-200 lg:col-span-2">
                        <CardContent className="p-6">
                            <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-2 flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-emerald-700" /> Automated resume parsing
                            </h3>
                            <p className="text-sm text-stone-500 font-dmsans mb-4">
                                Upload your PDF or DOCX. Claude Sonnet 4.5 extracts your experience, skills and certifications.
                            </p>

                            <label
                                htmlFor="resume-upload"
                                className="block cursor-pointer rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 transition-colors p-8 text-center"
                            >
                                <Upload className="w-8 h-8 text-emerald-700 mx-auto mb-3" strokeWidth={1.5} />
                                <div className="font-medium text-stone-900">
                                    {uploading ? "Parsing your resume..." : "Drop your resume or click to upload"}
                                </div>
                                <div className="text-xs text-stone-500 mt-1 font-dmsans">.pdf, .docx, .txt · Max 5MB</div>
                                <input
                                    id="resume-upload"
                                    data-testid={CAREGIVER.resumeUploadInput}
                                    ref={fileRef}
                                    type="file"
                                    accept=".pdf,.docx,.txt"
                                    onChange={upload}
                                    disabled={uploading}
                                    className="hidden"
                                />
                            </label>
                            <div className="mt-3 flex justify-end">
                                <Button
                                    data-testid={CAREGIVER.resumeUploadBtn}
                                    onClick={() => fileRef.current?.click()}
                                    disabled={uploading}
                                    variant="outline"
                                    className="rounded-full"
                                >
                                    {uploading ? "Parsing..." : "Choose file"}
                                </Button>
                            </div>

                            {parsed && (
                                <div data-testid={CAREGIVER.resumeResults} className="mt-6 grid sm:grid-cols-2 gap-4">
                                    <ParsedField label="Name" value={parsed.name} />
                                    <ParsedField label="Experience" value={parsed.years_experience ? `${parsed.years_experience} yrs` : "—"} />
                                    <ParsedField label="Email" value={parsed.email} />
                                    <ParsedField label="Phone" value={parsed.phone} />
                                    <ParsedField label="Skills" value={(parsed.skills || []).slice(0, 8).join(" · ") || "—"} full />
                                    <ParsedField label="Certifications" value={(parsed.certifications || []).slice(0, 6).join(" · ") || "—"} full />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="rounded-2xl border-stone-200 bg-gradient-to-br from-orange-500 to-orange-700 text-white overflow-hidden relative grain-overlay">
                        <CardContent className="p-6 relative z-10">
                            <CalendarPlus className="w-8 h-8 text-white/90 mb-3" strokeWidth={1.5} />
                            <h3 className="font-outfit text-xl font-semibold">Sync your calendar</h3>
                            <p className="text-orange-50/90 text-sm font-dmsans mt-1">
                                {user?.google_connected
                                    ? "You're connected. Push claimed shifts straight to Google."
                                    : "Connect Google to auto-push claimed shifts to your calendar."}
                            </p>
                            <Button
                                data-testid={CAREGIVER.connectGoogleBtn}
                                onClick={connectGoogle}
                                className="mt-5 rounded-full bg-white text-orange-700 hover:bg-orange-50"
                            >
                                {user?.google_connected ? "Reconnect" : "Connect Google Calendar"} <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            <div className="mt-6 space-y-1">
                                <button
                                    onClick={() => nav("/vault")}
                                    className="w-full text-left text-sm text-orange-50/90 hover:text-white font-dmsans flex items-center justify-between py-2 border-t border-white/10"
                                >
                                    Document vault <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={() => nav("/notify")}
                                    className="w-full text-left text-sm text-orange-50/90 hover:text-white font-dmsans flex items-center justify-between py-2 border-t border-white/10"
                                >
                                    Notifications <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4">Open shifts</h3>
                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                {openShifts.length === 0 && (
                                    <p className="text-sm text-stone-500 font-dmsans">No open shifts right now.</p>
                                )}
                                {openShifts.map((s) => (
                                    <ShiftRow key={s.id} shift={s} action={
                                        <Button
                                            data-testid={CAREGIVER.claimShiftBtn}
                                            onClick={() => claim(s.id)}
                                            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-9 text-xs"
                                        >
                                            Claim
                                        </Button>
                                    } />
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4">My claimed shifts</h3>
                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                {mine.length === 0 && (
                                    <p className="text-sm text-stone-500 font-dmsans">You have not claimed any shifts yet.</p>
                                )}
                                {mine.map((s) => (
                                    <ShiftRow key={s.id} shift={s} action={
                                        <Button
                                            data-testid={CAREGIVER.pushCalendarBtn}
                                            onClick={() => push(s.id)}
                                            variant="outline"
                                            className="rounded-full h-9 text-xs"
                                        >
                                            <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Push to calendar
                                        </Button>
                                    } />
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Care requests (family marketplace) */}
                <div className="mt-6">
                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-1">Family care requests</h3>
                            <p className="text-xs text-stone-500 font-dmsans mb-4">
                                Requests from families matching your credential ({user?.credential}).
                            </p>
                            <div className="space-y-3">
                                {careRequests.length === 0 && (
                                    <p className="text-sm text-stone-500 font-dmsans">No family requests right now.</p>
                                )}
                                {careRequests.filter((r) => r.status === "open").map((r) => (
                                    <div key={r.id} className="p-4 border border-stone-200 rounded-xl flex items-start justify-between gap-3">
                                        <div>
                                            <div className="font-medium text-stone-900">{r.service_label}</div>
                                            <div className="text-xs text-stone-500 mt-1 font-dmsans">
                                                {r.state} · {r.hours_per_week} h/wk · ${(r.hourly_budget_cents / 100).toFixed(2)}/hr
                                                {r.required_credential && ` · ${r.required_credential}`}
                                            </div>
                                            {r.notes && <div className="text-xs text-stone-600 mt-1 font-dmsans line-clamp-2">{r.notes}</div>}
                                        </div>
                                        <Button
                                            data-testid="caregiver-match-request-btn"
                                            onClick={async () => {
                                                try {
                                                    await api.post(`/care/requests/${r.id}/match`);
                                                    toast.success("Matched — you can now clock in from Care ops");
                                                    load();
                                                } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                                            }}
                                            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-9 text-xs shrink-0"
                                        >
                                            Accept
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Care ops panel: visits, care plan, meds, compliance, profile, timesheet */}
                <CareOpsPanel
                    role="caregiver"
                    careRequestId={careRequests.find((r) => r.matched_caregiver_id === user?.id && (r.status === "matched" || r.status === "in_progress"))?.id}
                />
            </main>
        </div>
    );
}

function ParsedField({ label, value, full }) {
    return (
        <div className={`p-4 rounded-xl bg-stone-50 border border-stone-100 ${full ? "sm:col-span-2" : ""}`}>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-1">{label}</div>
            <div className="text-sm text-stone-800 font-dmsans">{value || "—"}</div>
        </div>
    );
}

function ShiftRow({ shift, action }) {
    return (
        <div className="p-4 border border-stone-200 rounded-xl hover:bg-stone-50 transition-colors">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-medium text-stone-900 truncate">{shift.title}</div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-stone-500 font-dmsans">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {shift.location}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(shift.start).toLocaleString()}</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${((shift.rate_cents || 0) / 100).toFixed(2)}</span>
                    </div>
                </div>
                <div className="shrink-0">{action}</div>
            </div>
        </div>
    );
}
